import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useState } from 'react';
import { User, Loader2, MessageSquare, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { JobKanbanColumn, JobCVCard } from '../types';
import { KanbanColumn } from './KanbanColumn';

interface KanbanBoardProps {
  columns: JobKanbanColumn[];
  jobId?: string;
  onMoveCV?: (cvId: string, fromColumnId: string, toColumnId: string) => void;
  onBulkMoveCV?: (cvIds: string[], fromColumnId: string, toColumnId: string) => void;
  selectedCVIds?: Set<string>;
  onToggleSelect?: (cvId: string) => void;
  onToggleSelectAllInRound?: (roundId: string) => void;
  getIsDisabled?: (cv: JobCVCard) => boolean;
}

// Original single CV card for drag overlay
function SingleCVDragOverlay({ cv }: { cv: JobCVCard }) {
  return (
    <div
      className={`bg-white rounded-lg border p-3 shadow-lg w-64 ${
        cv.is_rejected ? 'border-red-300 bg-red-50' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-2 overflow-hidden">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 min-w-0">
            <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <span
              className={`text-sm font-medium text-gray-900 truncate ${
                cv.is_rejected ? 'line-through text-gray-500' : ''
              }`}
            >
              {cv.name}
            </span>
          </div>

          {/* Contact info */}
          {(cv.email || cv.phone) && (
            <div className="mt-1 text-xs text-gray-500 space-y-0.5 ml-6 overflow-hidden">
              {cv.email && <div className="truncate">{cv.email}</div>}
              {cv.phone && <div>{cv.phone}</div>}
            </div>
          )}

          {/* Stats row */}
          <div className="mt-2 ml-6 flex items-center gap-3 text-xs text-gray-400">
            {cv.note_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {cv.note_count}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(cv.linked_at), { locale: vi })}
            </span>
          </div>

          {/* Status badge - only show for pending/processing/error */}
          {(cv.processing_status === 'pending' ||
            cv.processing_status === 'processing' ||
            cv.processing_status === 'error') && (
            <div className="mt-2 ml-6">
              {cv.processing_status === 'error' ? (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
                  Error
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Processing
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Mini CV card for multi-drag overlay
function MiniCVCard({ cv, index }: { cv: JobCVCard; index: number }) {
  return (
    <div
      style={{ transform: `translateY(${index * 4}px) translateX(${index * 2}px)` }}
      className={`w-64 bg-white rounded-lg border-2 border-blue-500 p-2 shadow-lg ${
        cv.is_rejected ? 'bg-red-50' : ''
      }`}
    >
      <div className="flex items-start gap-2 overflow-hidden">
        <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium truncate block ${cv.is_rejected ? 'line-through text-gray-500' : 'text-gray-900'}`}>
            {cv.name}
          </span>
          {cv.email && <div className="text-xs text-gray-500 truncate">{cv.email}</div>}
        </div>
      </div>
    </div>
  );
}

// Multi CV drag overlay
function MultiCVDragOverlay({ cvs, count }: { cvs: JobCVCard[]; count: number }) {
  const displayCVs = cvs.slice(0, 3); // Show max 3 cards
  const remaining = count - displayCVs.length;

  return (
    <div className="relative">
      {/* Show stacked cards */}
      <div className="relative space-y-1">
        {displayCVs.map((cv, index) => (
          <MiniCVCard key={cv.id} cv={cv} index={index} />
        ))}
      </div>

      {/* Count badge - show total count */}
      <div className="absolute -top-3 -right-3 bg-blue-600 text-white text-sm font-bold rounded-lg px-2.5 py-1 shadow-lg border-2 border-white z-10">
        {count} CVs{remaining > 0 && ` (+${remaining})`}
      </div>
    </div>
  );
}

export function KanbanBoard({
  columns,
  jobId,
  onMoveCV,
  onBulkMoveCV,
  selectedCVIds,
  onToggleSelect,
  onToggleSelectAllInRound,
  getIsDisabled,
}: KanbanBoardProps) {
  const [draggingMultiple, setDraggingMultiple] = useState(false);
  const [multiDragCount, setMultiDragCount] = useState(0);
  const [draggingCVs, setDraggingCVs] = useState<JobCVCard[]>([]);
  const { t } = useTranslation('ats');

  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <p className="text-gray-500">{t('kanban.noPipeline')}</p>
      </div>
    );
  }

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const cv = active.data.current?.cv as JobCVCard | undefined;
    const cvId = active.id as string;

    if (cv) {
      // Check if we're dragging a selected CV with multiple selections
      const isDraggingSelected = selectedCVIds?.has(cvId);
      if (isDraggingSelected && selectedCVIds && selectedCVIds.size > 1) {
        setDraggingMultiple(true);
        setMultiDragCount(selectedCVIds.size);

        // Collect all CVs that are selected from all columns
        const allSelectedCVs: JobCVCard[] = [];
        columns.forEach((column) => {
          column.cvs.forEach((columnCV) => {
            if (selectedCVIds.has(columnCV.id)) {
              allSelectedCVs.push(columnCV);
            }
          });
        });
        setDraggingCVs(allSelectedCVs);
      } else {
        setDraggingMultiple(false);
        setMultiDragCount(0);
        setDraggingCVs([cv]);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingMultiple(false);
    setMultiDragCount(0);
    setDraggingCVs([]);

    if (!over) return;

    const cvId = active.id as string;
    const fromColumnId = active.data.current?.columnId as string;
    const toColumnId = over.id as string;

    // Don't do anything if dropped in the same column
    if (fromColumnId === toColumnId) return;

    // Check if we're dragging a selected CV and there are multiple selected
    const isDraggingSelected = selectedCVIds?.has(cvId);
    const shouldBulkMove = isDraggingSelected && selectedCVIds && selectedCVIds.size > 1;

    // If dragging multiple selected CVs, use bulk move
    if (shouldBulkMove && onBulkMoveCV) {
      onBulkMoveCV(Array.from(selectedCVIds), fromColumnId, toColumnId);
    } else if (onMoveCV) {
      // Single CV move
      onMoveCV(cvId, fromColumnId, toColumnId);
    }
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 h-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            jobId={jobId}
            selectedCVIds={selectedCVIds}
            onToggleSelect={onToggleSelect}
            onToggleSelectAllInRound={onToggleSelectAllInRound}
            isDraggingMultiple={draggingMultiple}
            getIsDisabled={getIsDisabled}
          />
        ))}
      </div>

      <DragOverlay>
        {draggingCVs.length > 0 &&
          (draggingMultiple ? (
            <MultiCVDragOverlay cvs={draggingCVs} count={multiDragCount} />
          ) : (
            <SingleCVDragOverlay cv={draggingCVs[0]} />
          ))}
      </DragOverlay>
    </DndContext>
  );
}
